import { TextKey } from '../../i18n';

/** Aviso tal como lo entrega el API (`GET /api/notifications`). */
export interface AppNotification {
  id: number;
  category: string;
  type: string;
  severity: 'info' | 'warning' | 'high';
  collectId: number | null;
  data: Record<string, any> | null;
  link: string | null;
  read: boolean;
  createdAt: string;
}

type T = (key: TextKey, vars?: Record<string, string | number>) => string;

const has = (t: T, key: string) => t(key as TextKey) !== key;

/** Título y texto del aviso en el idioma de quien lo lee, armados con los datos del aviso. */
export const notificationText = (n: AppNotification, t: T): { title: string; body: string } => {
  const d = n.data ?? {};
  const where = d.routeName ? t('notifications.where', { route: d.routeName }) : '';
  switch (n.type) {
    case 'alert': {
      const kindKey = `alerts.kind.${d.kind}`;
      return {
        title: has(t, kindKey) ? t(kindKey as TextKey) : t('alerts.kind.client_changed'),
        body: [d.actorName, d.routeName].filter(Boolean).join(' · '),
      };
    }
    case 'admin_change': {
      const actionKey = `alerts.adminChange.${d.action}`;
      return {
        title: t('notifications.type.admin_change', { actor: d.actorName || t('notifications.someone') }),
        body: [has(t, actionKey) ? t(actionKey as TextKey) : t('alerts.kind.admin_change'), d.target].filter(Boolean).join(' · '),
      };
    }
    case 'approval_device':
      return { title: t('notifications.type.approval_device'), body: t('notifications.body.approval_device', { who: d.salesRepName || t('notifications.aSalesRep'), where }) };
    case 'approval_correction':
      return {
        title: t('notifications.type.approval_correction'),
        body: t(d.kind === 'delete' ? 'notifications.body.approval_correction_delete' : 'notifications.body.approval_correction', { who: d.requesterName || t('notifications.someone'), where }),
      };
    case 'daily_close_failed':
      return { title: t('notifications.type.daily_close_failed', { date: d.date ?? '' }), body: t('notifications.body.daily_close_failed') };
    case 'daily_summary':
      return {
        title: t('notifications.type.daily_summary', { date: d.date ?? '' }),
        body: t('notifications.body.daily_summary', { collected: d.collected ?? '0', payments: d.payments ?? 0, alerts: d.openAlerts ?? 0 }),
      };
    case 'storage_warning':
      // 30 días lleno: se bloquea subir fotos. Antes, el aviso dice desde qué día.
      if (d.blocked) return { title: t('notifications.type.storage_blocked'), body: t('notifications.body.storage_blocked') };
      return {
        title: t(Number(d.level) >= 100 ? 'notifications.type.storage_full' : 'notifications.type.storage_warning'),
        body: [
          t('notifications.body.storage_warning', { used: d.usedGb ?? 0, included: d.includedGb ?? 0 }),
          d.blockOn ? t('notifications.body.storage_blockOn', { date: d.blockOn }) : '',
        ].filter(Boolean).join(' '),
      };
    case 'referral_rewarded':
      return {
        title: t('notifications.type.referral_rewarded'),
        body: t('notifications.body.referral_rewarded', { company: d.companyName || t('notifications.someone'), pct: d.pct ?? 20, periods: d.periods ?? 2 }),
      };
    case 'correction_rejected':
      return { title: t('notifications.type.correction_rejected'), body: d.note ? String(d.note) : '' };
    case 'device_approved':
    case 'device_blocked':
    case 'correction_applied':
      return { title: t(`notifications.type.${n.type}` as TextKey), body: t(`notifications.body.${n.type}` as TextKey) };
    default:
      return { title: t('notifications.title'), body: '' };
  }
};

/** «hace 5 min», «ayer», … en el idioma de la persona. */
export const relativeTime = (iso: string, lang: string, now = Date.now()) => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.round((then - now) / 1000);
  const abs = Math.abs(seconds);
  try {
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    if (abs < 60) return rtf.format(seconds, 'second');
    if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
    if (abs < 7 * 86400) return rtf.format(Math.round(seconds / 86400), 'day');
    return new Intl.DateTimeFormat(lang, { dateStyle: 'medium' }).format(new Date(then));
  } catch {
    return new Date(then).toLocaleString();
  }
};
